import React, { createContext, useState, useContext, ReactNode, useCallback } from "react";
import { AxiosResponse } from "axios";
import { calculateFRAPI, getDefaultPaymentTermsAPI } from "services/api/haulpay";
import { IFRCalculationResult, IPaymentSpeed } from "models/HaulPay";
import { showErrorMessage } from "../helpers/toastMessage";
import { getInvitedCarrierPaymentTermsAPI } from "../services/api/joinHaulpay";

interface BrokerContextType {
  defaultPaymentSettings: IPaymentSpeed[];
  isPaymentSettingsLoaded: boolean;
  paymentSettingsContextKey: string | null;
  getDefaultPaymentSettings: (factoringId: string) => Promise<void>;
  getBrokerDefaultPaymentSettings: (factoringId: string) => Promise<void>;
  calculateFR: (data: CalculateFRParams) => Promise<IFRCalculationResult>;
  frCalculation: IFRCalculationResult | undefined;
  getPaymentSpeedLabel: (value: string | { value?: string } | undefined) => string | undefined;
}

interface CalculateFRParams {
  debtor: string;
  factored: boolean;
  invoice_amount: number;
  apply_reserve_holdback?: boolean;
  bill_out_only?: boolean;
  contract_type?: string;
  create_rates_revision?: boolean;
  factoring_fee_split_rate?: number;
  payment_profile?: string;
  payment_profile_amount?: number;
  payment_profile_invoice_amount?: number;
  payment_speed?: number;
  self_finance_requested?: boolean;
  discount_rate_comfreight?: number;
}

const BrokerContext = createContext<BrokerContextType | undefined>(undefined);

export const BrokerProvider = ({ children }: { children: ReactNode }) => {
  const [defaultPaymentSettings, setDefaultPaymentSettings] = useState<IPaymentSpeed[]>([]);
  const [isPaymentSettingsLoaded, setIsPaymentSettingsLoaded] = useState(false);
  const [paymentSettingsContextKey, setPaymentSettingsContextKey] = useState<string | null>(null);
  const [frCalculation, setFrCalculation] = useState<IFRCalculationResult>();

  const getDefaultPaymentSettings = useCallback(async (factoringId: string) => {
    try {
      const paymentSettingsResponse: AxiosResponse<IPaymentSpeed[]> =
        await getDefaultPaymentTermsAPI(factoringId);
      setDefaultPaymentSettings(
        paymentSettingsResponse.data.filter((item) => item.payee === "carrier")
      );
      setIsPaymentSettingsLoaded(true);
      setPaymentSettingsContextKey(`default:${factoringId}`);
    } catch {
      showErrorMessage("Failed to fetch payment terms");
    }
  }, []);

  const getBrokerDefaultPaymentSettings = useCallback(async (factoringId: string) => {
    try {
      const paymentSettingsResponse: AxiosResponse<IPaymentSpeed[]> =
        await getInvitedCarrierPaymentTermsAPI(factoringId);
      setDefaultPaymentSettings(paymentSettingsResponse.data);
      setIsPaymentSettingsLoaded(true);
      setPaymentSettingsContextKey(`broker:${factoringId}`);
    } catch {
      showErrorMessage("Failed to fetch payment terms");
    }
  }, []);

  const calculateFR = async (data: CalculateFRParams) => {
    try {
      const response = await calculateFRAPI(data);
      setFrCalculation(response.data);
      return response.data;
    } catch {
      showErrorMessage("Failed to fetch calculation");
    }
  };

  // Get payment speed label from fetched settings - useCallback so it updates when settings change
  const getPaymentSpeedLabel = useCallback(
    (value: string | { value?: string } | undefined): string | undefined => {
      if (!value) return undefined;

      // Get the value string to look up
      const val = typeof value === "object" ? (value as any)?.value : value;
      if (!val) return undefined;

      // Extract days number from value like "ach_7_day" -> 7
      const dayMatch = String(val).match(/ach_(\d+)_day/);
      const dayNum = dayMatch ? parseInt(dayMatch[1], 10) : null;

      // Find by matching payout_days directly
      const fetchedOption = defaultPaymentSettings?.find((term) => {
        // Handle ach_1_day matching both payout_days 0 and 1
        if (val === "ach_1_day") {
          return term.payout_days === 0 || term.payout_days === 1;
        }
        return term.payout_days === dayNum;
      });

      if (fetchedOption) {
        const days =
          fetchedOption.payout_days === 0 || fetchedOption.payout_days === 1
            ? "1 Day"
            : `${fetchedOption.payout_days} Days`;
        const rate = fetchedOption.discount_rate || fetchedOption.carrier_rate || 0;
        if (rate === 0) {
          return `${days} (Free)`;
        }
        return `${days} (${rate}% Quick Pay Fee)`;
      }

      // Fallback: use label from value object if fetched settings not available
      if (typeof value === "object" && (value as any)?.label) {
        return (value as any).label;
      }

      return undefined;
    },
    [defaultPaymentSettings]
  );

  return (
    <BrokerContext.Provider
      value={{
        defaultPaymentSettings,
        isPaymentSettingsLoaded,
        paymentSettingsContextKey,
        getDefaultPaymentSettings,
        calculateFR,
        frCalculation,
        getBrokerDefaultPaymentSettings,
        getPaymentSpeedLabel
      }}>
      {children}
    </BrokerContext.Provider>
  );
};

export const useBrokerContext = (): BrokerContextType => {
  const context = useContext(BrokerContext);
  if (!context) {
    throw new Error("useBrokerContext must be used within a BrokerProvider");
  }
  return context;
};
